import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  Container,
  CircularProgress,
  Chip,
  Avatar
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Business as BusinessIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import api from '../services/api';

const ManualCapture = () => {
  const [profiles, setProfiles] = useState([]);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [captureResults, setCaptureResults] = useState([]);

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    try {
      const response = await api.get('/api/profiles');
      setProfiles(response.data);
    } catch (error) {
      console.error('Error fetching profiles:', error);
      setError('Failed to load profiles');
    }
  };

  const handleCaptureAll = async () => {
    setCapturing(true);
    setError(null);
    setSuccess(null);
    setCaptureResults([]);
    
    try {
      const profileIds = profiles.map((p) => p._id);
      const response = await api.post('/api/snapshots/capture', { profileIds });
      setSuccess('Successfully triggered capture for all profiles');
      // Normalize: if server returns aggregate object, synthesize per-profile entries
      const serverResults = response.data?.results;
      const results = Array.isArray(serverResults)
        ? serverResults
        : profileIds.map((id) => ({ profileId: id, status: response.data?.success ? 'success' : 'unknown' }));
      setCaptureResults(results);
    } catch (error) {
      console.error('Error capturing all profiles:', error);
      setError('Failed to capture all profiles');
    } finally {
      setCapturing(false);
    }
  };

  const handleCaptureSingle = async (profileId) => {
    setCapturing(true);
    setError(null);
    setSuccess(null);
    
    try {
      await api.post('/api/snapshots/capture', { profileId });
      setSuccess(`Successfully captured snapshot for profile ${profileId}`);
    } catch (error) {
      console.error('Error capturing single profile:', error);
      setError(`Failed to capture profile ${profileId}`);
    } finally {
      setCapturing(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      default:
        return <TimelineIcon color="action" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography 
          variant="h2" 
          component="h1" 
          sx={{ 
            fontWeight: 'bold',
            color: '#ffffff',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
            mb: 2
          }}
          gutterBottom
        >
          Manual Snapshot Capture
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.9)',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
            mb: 4
          }}
        >
          Manually trigger snapshot captures for your Google Business Profiles
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Card className="modern-card" sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PlayArrowIcon color="primary" />
            Capture Actions
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={capturing ? <CircularProgress size={20} /> : <PlayArrowIcon />}
              onClick={handleCaptureAll}
              disabled={capturing}
              className="modern-button"
              size="large"
            >
              {capturing ? 'Capturing All...' : 'Capture All Profiles'}
            </Button>
            
            <Button
              variant="outlined"
              onClick={fetchProfiles}
              disabled={capturing}
              className="modern-button"
            >
              Refresh Profiles
            </Button>
          </Box>

          {/* Status Messages */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Profiles List */}
      <Card className="modern-card">
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" />
            Available Profiles ({profiles.length})
          </Typography>
          
          {profiles.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No profiles available
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Add some Google Business Profiles to get started
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {profiles.map((profile) => (
                <Grid item xs={12} sm={6} md={4} key={profile._id}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <BusinessIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a202c' }}>
                            {profile.name}
                          </Typography>
                          {profile.category && (
                            <Typography variant="body2" color="textSecondary">
                              {profile.category}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      
                      {profile.address && (
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          {profile.address}
                        </Typography>
                      )}
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip
                          label={profile.isActive ? 'Active' : 'Inactive'}
                          color={profile.isActive ? 'success' : 'error'}
                          size="small"
                          className="modern-chip"
                        />
                        
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={capturing ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                          onClick={() => handleCaptureSingle(profile._id)}
                          disabled={capturing}
                          className="modern-button"
                        >
                          Capture
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Capture Results */}
      {captureResults.length > 0 && (
        <Card className="modern-card" sx={{ mt: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimelineIcon color="primary" />
              Capture Results
            </Typography>
            
            <Grid container spacing={2}>
              {captureResults.map((result, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {getStatusIcon(result.status)}
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {result.profileId}
                        </Typography>
                      </Box>
                      
                      <Chip
                        label={result.status}
                        color={getStatusColor(result.status)}
                        size="small"
                        className="modern-chip"
                      />
                      
                      {result.message && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                          {result.message}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default ManualCapture; 